import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../config/aws-config';
import { handleAuthError } from '../utils/authUtils';

/**
 * 新設計: Family-groups API専用サービス
 * 旧weddingAPIServiceを完全に置き換える
 */
class FamilyAPIService {
  constructor() {
    this.baseURL = awsConfig.apiGateway.URL;
  }

  /**
   * 認証ヘッダー取得
   */
  async getAuthHeaders() {
    try {
      console.log('🔄 FamilyAPI: 認証ヘッダー取得開始');
      
      const session = await fetchAuthSession({ forceRefresh: false });
      console.log('✅ FamilyAPI: 初回セッション取得完了');
      console.log('📝 FamilyAPI: Session tokens 存在:', !!session.tokens);
      console.log('📝 FamilyAPI: Access token 存在:', !!session.tokens?.accessToken);
      console.log('📝 FamilyAPI: ID token 存在:', !!session.tokens?.idToken);
      
             if (!session.tokens?.idToken) {
         console.error('❌ FamilyAPI: ID token が存在しません');
         throw new Error('認証トークンが取得できませんでした');
       }

       const token = session.tokens.idToken.toString();
              console.log('✅ FamilyAPI: ID token 取得完了 - 長さ:', token.length);
      
      // JWTトークンの詳細解析
      try {
        const tokenParts = token.split('.');
        console.log('📝 FamilyAPI: JWT parts 数:', tokenParts.length);
        
        if (tokenParts.length === 3) {
          const header = JSON.parse(atob(tokenParts[0]));
          const payload = JSON.parse(atob(tokenParts[1]));
          
          console.log('📝 FamilyAPI: JWT Header:', header);
          console.log('📝 FamilyAPI: JWT Algorithm:', header.alg);
          console.log('📝 FamilyAPI: JWT Token Type:', header.typ);
          
          console.log('📝 FamilyAPI: JWT Payload iss:', payload.iss);
          console.log('📝 FamilyAPI: JWT Payload sub:', payload.sub);
          console.log('📝 FamilyAPI: JWT Payload username:', payload.username);
          console.log('📝 FamilyAPI: JWT Payload cognito:groups:', payload['cognito:groups']);
          console.log('📝 FamilyAPI: JWT Payload exp:', new Date(payload.exp * 1000));
          console.log('📝 FamilyAPI: JWT Payload iat:', new Date(payload.iat * 1000));
          console.log('📝 FamilyAPI: 現在時刻:', new Date());
          
          const isExpired = payload.exp < (Date.now() / 1000);
          console.log('📝 FamilyAPI: Token 期限切れ:', isExpired);
          
          if (isExpired) {
            console.log('⚠️ FamilyAPI: Token期限切れ - 強制リフレッシュ実行');
            const refreshedSession = await fetchAuthSession({ forceRefresh: true });
            
                         if (!refreshedSession.tokens?.idToken) {
               console.error('❌ FamilyAPI: リフレッシュ後もID token が取得できません');
               throw new Error('認証トークンのリフレッシュに失敗しました');
             }
             
                          const refreshedToken = refreshedSession.tokens.idToken.toString();
             console.log('✅ FamilyAPI: リフレッシュ後 ID token 取得完了 - 長さ:', refreshedToken.length);
            
            const headers = {
              'Authorization': `Bearer ${refreshedToken}`,
              'Content-Type': 'application/json'
            };
            
            console.log('✅ FamilyAPI: リフレッシュ後認証ヘッダー生成完了');
            return headers;
          }
        }
      } catch (parseError) {
        console.warn('⚠️ FamilyAPI: JWT解析エラー:', parseError);
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      console.log('✅ FamilyAPI: 認証ヘッダー生成完了');
      console.log('📝 FamilyAPI: Authorization header プレフィックス:', headers.Authorization.substring(0, 20) + '...');
      return headers;
    } catch (error) {
      console.error('❌ FamilyAPI: 認証ヘッダー取得エラー:', error);
      console.error('❌ FamilyAPI: エラータイプ:', error.constructor.name);
      console.error('❌ FamilyAPI: エラーメッセージ:', error.message);
      
      // 認証エラーの自動復旧を試行
      await handleAuthError(error);
      
      throw new Error('認証情報の取得に失敗しました');
    }
  }

  /**
   * API リクエスト実行
   */
  async makeRequest(endpoint, options = {}) {
    try {
      console.log(`FamilyAPI: Starting request to ${endpoint}`);
      console.log('FamilyAPI: Request options:', JSON.stringify(options, null, 2));
      
      const authHeaders = await this.getAuthHeaders();
      console.log('FamilyAPI: Auth headers obtained:', Object.keys(authHeaders));
      
      const config = {
        ...options,
        headers: {
          ...authHeaders,
          ...(options.headers || {})
        }
      };

      const fullUrl = `${this.baseURL}${endpoint}`;
      console.log('FamilyAPI: Full URL:', fullUrl);
      console.log('FamilyAPI: Final config:', JSON.stringify(config, null, 2));

      const response = await fetch(fullUrl, config);
      console.log('FamilyAPI: Response status:', response.status);
      console.log('FamilyAPI: Response statusText:', response.statusText);
      console.log('FamilyAPI: Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        console.error('FamilyAPI: Response not OK, attempting to parse error data...');
        const errorData = await response.json().catch((parseError) => {
          console.error('FamilyAPI: Failed to parse error response as JSON:', parseError);
          return {};
        });
        console.error('FamilyAPI: Error response:', errorData);
        
        // ユーザーが削除されたことを検知（403 Forbidden または特定のエラーメッセージ）
        if (response.status === 403 || 
            (errorData.error && errorData.error.includes('User not found')) ||
            (errorData.error && errorData.error.includes('not authorized')) ||
            (errorData.message && errorData.message.includes('User not found'))) {
          console.warn('FamilyAPI: User appears to have been deleted, triggering logout...');
          
          // ユーザー削除を検知した場合の処理
          this.handleUserDeleted();
        }
        
        // エラーメッセージを詳細に保持
        let errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        
        // HTTP 400エラーで具体的なメッセージがある場合、それを優先
        if (response.status === 400 && (errorData.message || errorData.error)) {
          errorMessage = errorData.message || errorData.error;
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = {
          status: response.status,
          statusText: response.statusText,
          data: errorData
        };
        
        console.error('FamilyAPI: Throwing error:', error);
        throw error;
      }

      // レスポンスが空の場合（204 No Content等）
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        console.log('FamilyAPI: Empty response (204 or no content)');
        return null;
      }

      const responseData = await response.json();
      console.log('FamilyAPI: Response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('FamilyAPI: Request failed:', error);
      console.error('FamilyAPI: Error type:', typeof error);
      console.error('FamilyAPI: Error constructor:', error.constructor.name);
      
      // 401エラーまたはJWT関連エラーの場合、認証キャッシュクリアを試行
      if (error.status === 401 || 
          (error.message && error.message.includes('token')) ||
          (error.message && error.message.includes('unauthorized'))) {
        console.log('🔧 FamilyAPI: 401エラー検出 - 認証復旧を試行');
        await handleAuthError(error);
      }
      
      throw error;
    }
  }

  // ===========================================
  // ファミリーアカウント管理
  // ===========================================

  /**
   * ファミリーアカウント一覧取得
   */
  async getFamilyGroups() {
    return await this.makeRequest('/family-groups', { method: 'GET' });
  }

  /**
   * 新しいファミリーアカウント作成
   */
  async createFamilyGroup(familyName, description = '') {
    return await this.makeRequest('/family-groups', {
      method: 'POST',
      body: JSON.stringify({
        familyName,
        description
      })
    });
  }

  /**
   * 招待トークン生成
   */
  async generateInviteToken(groupName) {
    console.log('🔄 FamilyAPI: generateInviteToken called with groupName:', groupName);
    
    try {
      const response = await this.makeRequest('/family-groups/invite-token', {
        method: 'POST',
        body: JSON.stringify({
          groupName
        })
      });
      
      console.log('✅ FamilyAPI: generateInviteToken success:', response);
      return response;
    } catch (error) {
      console.error('❌ FamilyAPI: generateInviteToken error:', error);
      throw error;
    }
  }

  /**
   * 招待トークンによる参加
   */
  async joinByInviteToken(inviteToken) {
    try {
      console.log('FamilyAPI: joinByInviteToken - Starting with token:', inviteToken);
      
      if (!inviteToken || inviteToken.trim() === '') {
        throw new Error('招待トークンが指定されていません');
      }
      
      const response = await this.makeRequest('/family-groups/join-by-token', {
        method: 'POST',
        body: JSON.stringify({
          inviteToken: inviteToken.trim()
        })
      });
      
      console.log('FamilyAPI: joinByInviteToken - Success response:', response);
      
      // レスポンスの形式を確認し、必要に応じて変換
      if (response && response.familyGroup) {
        // ファミリーグループが返された場合、ウェディンググループ形式に変換
        const weddingGroup = this.convertFamilyGroupToWeddingGroup(response.familyGroup);
        return { weddingGroup, message: response.message };
      } else if (response && response.message) {
        // メッセージのみの場合（既に参加済みなど）
        return response;
      }
      
      return response;
    } catch (error) {
      console.error('FamilyAPI: joinByInviteToken - Error:', error);
      
      // エラーの詳細をログに出力
      if (error.response) {
        console.error('FamilyAPI: joinByInviteToken - Response error:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        });
      }
      
      throw error;
    }
  }

  /**
   * ファミリーアカウントから脱退
   */
  async leaveFamilyGroup(groupId) {
    return await this.makeRequest(`/family-groups/${groupId}`, {
      method: 'DELETE'
    });
  }

  /**
   * ファミリーグループから他のメンバーを削除（オーナーのみ）
   */
  async removeMember(groupName, memberUserId) {
    console.log('🔄 FamilyAPI: removeMember called with:', { groupName, memberUserId });
    
    try {
      const response = await this.makeRequest(`/family-groups/${encodeURIComponent(groupName)}/members/${encodeURIComponent(memberUserId)}`, {
        method: 'DELETE'
      });
      
      console.log('✅ FamilyAPI: removeMember success:', response);
      return response;
    } catch (error) {
      console.error('❌ FamilyAPI: removeMember error:', error);
      throw error;
    }
  }

  /**
   * ファミリーアカウント削除（オーナーのみ）
   */
  async deleteFamily(groupName) {
    console.log('🔄 FamilyAPI: deleteFamily called with groupName:', groupName);
    
    try {
      const response = await this.makeRequest(`/family-groups/${encodeURIComponent(groupName)}`, {
        method: 'DELETE'
      });
      
      console.log('✅ FamilyAPI: deleteFamily success:', response);
      return response;
    } catch (error) {
      console.error('❌ FamilyAPI: deleteFamily error:', error);
      throw error;
    }
  }

  // ===========================================
  // タスク管理
  // ===========================================

  /**
   * ファミリーグループのタスク一覧取得
   */
  async getFamilyTasks(groupName) {
    const endpoint = `/family-groups/${encodeURIComponent(groupName)}/tasks`;
    const response = await this.makeRequest(endpoint, { method: 'GET' });
    console.log('FamilyAPI: getFamilyTasks response:', response);
    
    // レスポンスの形式を確認
    if (response && response.tasks) {
      return response.tasks;
    } else if (Array.isArray(response)) {
      return response;
    } else {
      console.warn('FamilyAPI: getFamilyTasks - unexpected response format:', response);
      return [];
    }
  }

  /**
   * ファミリーグループのタスク作成
   */
  async createFamilyTask(groupName, taskData) {
    const endpoint = `/family-groups/${encodeURIComponent(groupName)}/tasks`;
    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
    console.log('FamilyAPI: createFamilyTask response:', response);
    
    // レスポンスからタスクデータを抽出
    if (response && response.task) {
      return response.task;
    } else if (response) {
      return response;
    } else {
      console.error('FamilyAPI: createFamilyTask - unexpected response format:', response);
      return null;
    }
  }

  /**
   * ファミリーグループのタスク更新
   */
  async updateFamilyTask(groupName, taskId, updateData) {
    const endpoint = `/family-groups/${encodeURIComponent(groupName)}/tasks/${encodeURIComponent(taskId)}`;
    const response = await this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    return response.task;
  }

  /**
   * ファミリーグループのタスク削除
   */
  async deleteFamilyTask(groupName, taskId) {
    const endpoint = `/family-groups/${encodeURIComponent(groupName)}/tasks/${encodeURIComponent(taskId)}`;
    return await this.makeRequest(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * タスク作成
   */
  async createTask(taskData) {
    // 一般的なタスクAPIを使用
    const response = await this.makeRequest('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
    
    return response?.task || null;
  }

  /**
   * タスク更新
   */
  async updateTask(taskId, updateData) {
    const response = await this.makeRequest(`/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    
    return response?.task || null;
  }

  /**
   * タスク削除
   */
  async deleteTask(taskId) {
    await this.makeRequest(`/tasks/${taskId}`, { method: 'DELETE' });
    return true;
  }

  /**
   * タスク一覧取得
   */
  async getTasks(filters = {}) {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value);
      }
    });

    const endpoint = `/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.makeRequest(endpoint, { method: 'GET' });
    
    const tasks = response?.tasks || [];
    return tasks;
  }

  // ===========================================
  // 設定管理
  // ===========================================

  /**
   * ファミリーグループの設定取得
   */
  async getFamilySettings(groupName) {
    return await this.makeRequest(`/family-groups/${groupName}/settings`, {
      method: 'GET'
    });
  }

  /**
   * ファミリーグループの設定更新
   */
  async updateFamilySettings(groupName, settings) {
    return await this.makeRequest(`/family-groups/${groupName}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  }

  /**
   * ファミリー名更新
   */
  async updateFamilyName(groupName, familyName) {
    return await this.makeRequest(`/family-groups/${groupName}/name`, {
      method: 'PUT',
      body: JSON.stringify({ familyName })
    });
  }

  /**
   * ユーザー設定取得
   */
  async getUserSettings() {
    const response = await this.makeRequest('/settings', { method: 'GET' });
    return response?.settings || {};
  }

  /**
   * ユーザー設定保存
   */
  async saveUserSettings(settingsData) {
    const response = await this.makeRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
    return response?.settings || {};
  }

  /**
   * ユーザーのプリセットタスクテンプレートを取得
   */
  async getPresetTasks() {
    const response = await this.makeRequest('/presets', {
      method: 'GET'
    });
    return response.presets || [];
  }

  // ===========================================
  // ユーティリティ
  // ===========================================

  /**
   * APIヘルスチェック
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/family-groups`, {
        method: 'OPTIONS'
      });
      return response.ok;
    } catch (error) {
      console.error('API Health Check Error:', error);
      return false;
    }
  }

  /**
   * Family ID可用性チェック
   */
  async checkFamilyIdAvailability(familyId) {
    try {
      // ファミリーグループ一覧を取得してチェック
      const response = await this.getFamilyGroups();
      return !response.familyGroups || response.familyGroups.length === 0;
    } catch (error) {
      console.error('FamilyAPI: checkFamilyIdAvailability error:', error);
      return true; // エラー時は利用可能として扱う
    }
  }

  // ===========================================
  // 互換性メソッド（段階的移行用）
  // ===========================================

  /**
   * ウェディンググループ取得（互換性のため）
   */
  async getWeddingGroup() {
    try {
      const response = await this.getFamilyGroups();
      console.log('FamilyAPI: getWeddingGroup - Raw response:', response);
      
      if (response.familyGroups && response.familyGroups.length > 0) {
        // 最初のファミリーグループをウェディンググループとして変換
        const primaryFamilyGroup = response.familyGroups[0];
        const weddingGroup = this.convertFamilyGroupToWeddingGroup(primaryFamilyGroup);
        
        console.log('FamilyAPI: getWeddingGroup - Converted to wedding group:', weddingGroup);
        return { weddingGroup };
      } else {
        console.log('FamilyAPI: getWeddingGroup - No family groups found');
        return { weddingGroup: null };
      }
    } catch (error) {
      console.error('FamilyAPI: getWeddingGroup error:', error);
      return { weddingGroup: null };
    }
  }

  /**
   * ウェディンググループ作成（互換性のため）
   */
  async createWeddingGroup(familyId, familyName = '', weddingDate = '', venue = '', budget = 0, estimatedGuests = 0) {
    try {
      // ファミリーグループを作成
      const response = await this.createFamilyGroup(
        familyName || familyId,
        `${familyName || familyId}の結婚準備グループ`
      );
      
      console.log('FamilyAPI: createWeddingGroup - Family group created:', response);
      
      if (response.familyGroup || response.groupName) {
        const familyGroup = response.familyGroup || response;
        
        // 設定があれば更新
        if (weddingDate || venue || budget > 0 || estimatedGuests > 0) {
          try {
            await this.updateFamilySettings(familyGroup.groupName || response.groupName, {
              weddingDate,
              venue,
              budget,
              estimatedGuests
            });
          } catch (settingsError) {
            console.warn('FamilyAPI: createWeddingGroup - Settings update failed:', settingsError);
          }
        }
        
        const weddingGroup = this.convertFamilyGroupToWeddingGroup(familyGroup);
        return { weddingGroup };
      }
      
      throw new Error('ファミリーグループの作成に失敗しました');
    } catch (error) {
      console.error('FamilyAPI: createWeddingGroup error:', error);
      throw error;
    }
  }

  /**
   * ファミリーグループをウェディンググループ形式に変換（互換性のため）
   */
  convertFamilyGroupToWeddingGroup(familyGroup) {
    if (!familyGroup) return null;
    
    return {
      id: familyGroup.groupName,
      familyName: familyGroup.familyName || familyGroup.groupName,
      groupName: familyGroup.groupName, // 新設計の重要なプロパティ
      settings: familyGroup.familySettings || {
        weddingDate: '',
        venue: '',
        budget: 0,
        estimatedGuests: 0
      },
      members: familyGroup.members || [],
      inviteToken: familyGroup.inviteToken,
      inviteLink: familyGroup.inviteLink,
      createdAt: familyGroup.createdDate,
      description: familyGroup.description
    };
  }

  /**
   * 設定取得（互換性のため）
   */
  async getSettings() {
    try {
      // まずファミリーグループを取得
      const weddingGroupResponse = await this.getWeddingGroup();
      if (weddingGroupResponse.weddingGroup) {
        return weddingGroupResponse.weddingGroup.settings || {};
      }
      
      // フォールバック: ユーザー設定を取得
      return await this.getUserSettings();
    } catch (error) {
      console.error('FamilyAPI: getSettings error:', error);
      return {};
    }
  }

  /**
   * 設定更新（互換性のため）
   */
  async updateSettings(settingsData) {
    try {
      // まずファミリーグループを取得
      const weddingGroupResponse = await this.getWeddingGroup();
      if (weddingGroupResponse.weddingGroup && weddingGroupResponse.weddingGroup.groupName) {
        const response = await this.updateFamilySettings(weddingGroupResponse.weddingGroup.groupName, settingsData);
        return response.familySettings || settingsData;
      }
      
      // フォールバック: ユーザー設定を更新
      return await this.saveUserSettings(settingsData);
    } catch (error) {
      console.error('FamilyAPI: updateSettings error:', error);
      throw error;
    }
  }

  /**
   * 招待リンク生成（互換性のため）
   */
  async generateInviteLink() {
    try {
      // まず現在のファミリーグループを取得
      const weddingGroupResponse = await this.getWeddingGroup();
      if (!weddingGroupResponse.weddingGroup) {
        throw new Error('ファミリーグループが見つかりません');
      }
      
      const groupName = weddingGroupResponse.weddingGroup.groupName;
      return await this.generateInviteToken(groupName);
    } catch (error) {
      console.error('FamilyAPI: generateInviteLink error:', error);
      throw error;
    }
  }

  /**
   * メンバー一覧取得（互換性のため）
   */
  async getMembers() {
    try {
      const weddingGroupResponse = await this.getWeddingGroup();
      return weddingGroupResponse.weddingGroup?.members || [];
    } catch (error) {
      console.error('FamilyAPI: getMembers error:', error);
      return [];
    }
  }

  /**
   * ユーザーが削除された場合の処理
   */
  handleUserDeleted() {
    console.log('FamilyAPI: Handling user deletion - clearing auth and redirecting...');
    
    try {
      // Cognitoからサインアウト
      import('../context/WeddingContext').then(({ useWeddingContext }) => {
        // 直接的なサインアウト処理
        if (window.localStorage) {
          // ローカルストレージをクリア
          window.localStorage.clear();
        }
        if (window.sessionStorage) {
          // セッションストレージをクリア
          window.sessionStorage.clear();
        }
        
        // ページをリロードしてログイン画面に戻る
        window.location.href = '/login?reason=account_deleted';
      });
    } catch (error) {
      console.error('FamilyAPI: Error during user deletion handling:', error);
      // フォールバック：強制的にページリロード
      window.location.href = '/login?reason=account_deleted';
    }
  }
}

// シングルトンインスタンス
const familyAPIService = new FamilyAPIService();

export default familyAPIService; 